import React, { useState, useEffect } from 'react';
import {
  Search,
  UserPlus,
  Eye,
  Edit,
  Trash2,
  Key,
  Loader,
  AlertCircle,
  RefreshCw,
  X,
  Shield
} from 'lucide-react';
import { adminManagementAPI } from '../utils/api';

const AdminManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [admins, setAdmins] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create', 'edit', 'view', 'password'
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    role_id: '',
    is_active: true
  });
  const [stats, setStats] = useState({
    total_admins: 0,
    active_admins: 0,
    inactive_admins: 0,
    total_roles: 0
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    totalPages: 1,
    totalDocs: 0
  });

  useEffect(() => {
    fetchData();
  }, [pagination.page, filterRole, filterStatus]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (pagination.page === 1) {
        fetchAdmins();
      } else {
        setPagination(prev => ({ ...prev, page: 1 }));
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchData = async () => {
    await Promise.all([fetchAdmins(), fetchRoles(), fetchStats()]);
  };

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm || undefined,
        role: filterRole !== 'all' ? filterRole : undefined,
        status: filterStatus !== 'all' ? filterStatus : undefined
      };

      const response = await adminManagementAPI.getAdmins(params);

      if (response.data.success) {
        const data = response.data.data;
        setAdmins(data.docs || []);
        setPagination(prev => ({
          ...prev,
          totalPages: data.totalPages || 1,
          totalDocs: data.totalDocs || 0
        }));
      }
    } catch (error) {
      console.error('Error fetching admins:', error);
      setError('Failed to load admins. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await adminManagementAPI.getRoles();
      if (response.data.success) {
        setRoles(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await adminManagementAPI.getAdminStats();
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleOpenModal = (mode, admin = null) => {
    setModalMode(mode);
    setSelectedAdmin(admin);

    if (mode === 'create') {
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        role_id: '',
        is_active: true
      });
    } else if (mode === 'edit' && admin) {
      setFormData({
        first_name: admin.first_name,
        last_name: admin.last_name,
        email: admin.email,
        password: '',
        role_id: admin.role?._id || admin.role || '',
        is_active: admin.is_active
      });
    } else if (mode === 'password') {
      setFormData({ ...formData, password: '', confirm_password: '' });
    }

    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedAdmin(null);
    setModalMode('create');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (modalMode === 'create') {
        await adminManagementAPI.createAdmin(formData);
        alert('Admin created successfully');
      } else if (modalMode === 'edit') {
        await adminManagementAPI.updateAdmin(selectedAdmin.id || selectedAdmin._id, formData);
        alert('Admin updated successfully');
      } else if (modalMode === 'password') {
        if (formData.password !== formData.confirm_password) {
          alert('Passwords do not match');
          return;
        }
        await adminManagementAPI.changePassword(selectedAdmin.id || selectedAdmin._id, formData.password);
        alert('Password changed successfully');
      }

      handleCloseModal();
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      alert(error.response?.data?.error?.message || 'Operation failed. Please try again.');
    }
  };

  const handleDelete = async (admin) => {
    if (!window.confirm(`Are you sure you want to delete ${admin.first_name} ${admin.last_name}?`)) {
      return;
    }

    try {
      await adminManagementAPI.deleteAdmin(admin.id || admin._id);
      alert('Admin deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting admin:', error);
      alert(error.response?.data?.error?.message || 'Failed to delete admin');
    }
  };

  const getStatusBadge = (admin) => {
    if (admin.is_active) {
      return <span className="badge-success">Active</span>;
    }
    return <span className="badge-error">Inactive</span>;
  };

  const formatDate = (dateString, objectId) => {
    if (dateString) {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
    }

    // Fallback to ObjectId timestamp
    if (objectId) {
      try {
        const timestamp = parseInt(objectId.substring(0, 8), 16) * 1000;
        const date = new Date(timestamp);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } catch (error) {
        return 'N/A';
      }
    }

    return 'N/A';
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="vetme-heading-big text-vetme-dark">Admin Management</h1>
          <p className="mt-2 text-vetme-gray-dark">
            Manage admin users, roles, and permissions
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <button
            onClick={() => fetchData()}
            className="btn-vetme-outline flex items-center"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => handleOpenModal('create')}
            className="btn-vetme-primary flex items-center"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Add Admin
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card-vetme p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-vetme-gray-dark">Total Admins</p>
              <p className="text-3xl font-bold text-vetme-dark mt-2">{stats.total_admins}</p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
              <Shield className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="card-vetme p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-vetme-gray-dark">Active</p>
              <p className="text-3xl font-bold text-green-600 mt-2">{stats.active_admins}</p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
              <Shield className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="card-vetme p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-vetme-gray-dark">Inactive</p>
              <p className="text-3xl font-bold text-orange-600 mt-2">{stats.inactive_admins}</p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center">
              <Shield className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>

        <div className="card-vetme p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-vetme-gray-dark">Total Roles</p>
              <p className="text-3xl font-bold text-vetme-dark mt-2">{stats.total_roles}</p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
              <Shield className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card-vetme p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-vetme-gray-dark w-5 h-5" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-vetme pl-10 w-full"
              />
            </div>
          </div>

          <div>
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="input-vetme w-full"
            >
              <option value="all">All Roles</option>
              {roles.map(role => (
                <option key={role._id || role.id} value={role._id || role.id}>
                  {role.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="input-vetme w-full"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-800 font-medium">Error</p>
              <p className="text-red-600 text-sm mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Admins Table */}
      <div className="card-vetme overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-vetme-gray-light">
            <thead className="bg-vetme-gray-lightest">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-vetme-gray-dark uppercase tracking-wider">
                  Admin
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-vetme-gray-dark uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-vetme-gray-dark uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-vetme-gray-dark uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-vetme-gray-dark uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-vetme-gray-dark uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-vetme-gray-light">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <Loader className="w-8 h-8 text-vetme-mint-dark animate-spin mx-auto" />
                    <p className="mt-2 text-vetme-gray-dark">Loading admins...</p>
                  </td>
                </tr>
              ) : admins.length > 0 ? (
                admins.map((admin) => (
                  <tr key={admin._id || admin.id} className="hover:bg-vetme-gray-lightest">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-vetme-mint-very-pale rounded-full flex items-center justify-center">
                          <span className="text-vetme-mint-dark font-medium">
                            {admin.first_name?.charAt(0)}{admin.last_name?.charAt(0)}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-vetme-dark">
                            {admin.first_name} {admin.last_name}
                          </div>
                          <div className="text-sm text-vetme-gray-dark">{admin.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800">
                        {admin.role?.name || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(admin)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-vetme-gray-dark">
                      {admin.last_login_at ? formatDate(admin.last_login_at) : 'Never'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-vetme-gray-dark">
                      {formatDate(admin.createdAt, admin.id || admin._id)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleOpenModal('view', admin)}
                          className="text-vetme-mint-dark hover:text-vetme-mint"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleOpenModal('edit', admin)}
                          className="text-blue-600 hover:text-blue-800"
                          title="Edit Admin"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleOpenModal('password', admin)}
                          className="text-orange-600 hover:text-orange-800"
                          title="Change Password"
                        >
                          <Key className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(admin)}
                          className="text-red-600 hover:text-red-800"
                          title="Delete Admin"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-vetme-gray-dark">
                    No admins found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="bg-white px-6 py-4 flex items-center justify-between border-t border-vetme-gray-light">
            <div className="text-sm text-vetme-gray-dark">
              Showing page {pagination.page} of {pagination.totalPages} ({pagination.totalDocs} total)
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                className="btn-vetme-outline disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page === pagination.totalPages}
                className="btn-vetme-outline disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
              onClick={handleCloseModal}
            ></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-vetme-dark px-6 py-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-white">
                      {modalMode === 'create' && 'Create Admin'}
                      {modalMode === 'edit' && 'Edit Admin'}
                      {modalMode === 'view' && 'Admin Details'}
                      {modalMode === 'password' && 'Change Password'}
                    </h3>
                    <button type="button" onClick={handleCloseModal} className="text-white hover:text-vetme-mint">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div className="bg-white px-6 py-4">
                  {modalMode === 'view' && selectedAdmin ? (
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm text-vetme-gray-dark">Name</p>
                        <p className="text-sm font-medium text-vetme-dark">
                          {selectedAdmin.first_name} {selectedAdmin.last_name}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-vetme-gray-dark">Email</p>
                        <p className="text-sm font-medium text-vetme-dark">{selectedAdmin.email}</p>
                      </div>
                      <div>
                        <p className="text-sm text-vetme-gray-dark">Role</p>
                        <p className="text-sm font-medium text-vetme-dark">{selectedAdmin.role?.name || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-vetme-gray-dark">Status</p>
                        {getStatusBadge(selectedAdmin)}
                      </div>
                    </div>
                  ) : modalMode === 'password' ? (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-vetme-gray-dark mb-2">
                          New Password *
                        </label>
                        <input
                          type="password"
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          className="input-vetme w-full"
                          required
                          minLength={8}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-vetme-gray-dark mb-2">
                          Confirm Password *
                        </label>
                        <input
                          type="password"
                          value={formData.confirm_password}
                          onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                          className="input-vetme w-full"
                          required
                          minLength={8}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-vetme-gray-dark mb-2">
                            First Name *
                          </label>
                          <input
                            type="text"
                            value={formData.first_name}
                            onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                            className="input-vetme w-full"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-vetme-gray-dark mb-2">
                            Last Name *
                          </label>
                          <input
                            type="text"
                            value={formData.last_name}
                            onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                            className="input-vetme w-full"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-vetme-gray-dark mb-2">
                          Email *
                        </label>
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="input-vetme w-full"
                          required
                        />
                      </div>

                      {modalMode === 'create' && (
                        <div>
                          <label className="block text-sm font-medium text-vetme-gray-dark mb-2">
                            Password *
                          </label>
                          <input
                            type="password"
                            value={formData.password}
                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            className="input-vetme w-full"
                            required={modalMode === 'create'}
                            minLength={8}
                          />
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-vetme-gray-dark mb-2">
                          Role *
                        </label>
                        <select
                          value={formData.role_id}
                          onChange={(e) => setFormData({ ...formData, role_id: e.target.value })}
                          className="input-vetme w-full"
                          required
                        >
                          <option value="">Select Role</option>
                          {roles.map(role => (
                            <option key={role._id || role.id} value={role._id || role.id}>
                              {role.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="is_active"
                          checked={formData.is_active}
                          onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                          className="w-4 h-4 text-vetme-mint border-gray-300 rounded focus:ring-vetme-mint"
                        />
                        <label htmlFor="is_active" className="ml-2 text-sm text-vetme-gray-dark">
                          Active
                        </label>
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
                  <button type="button" onClick={handleCloseModal} className="btn-vetme-outline">
                    {modalMode === 'view' ? 'Close' : 'Cancel'}
                  </button>
                  {modalMode !== 'view' && (
                    <button type="submit" className="btn-vetme-primary">
                      {modalMode === 'create' && 'Create Admin'}
                      {modalMode === 'edit' && 'Update Admin'}
                      {modalMode === 'password' && 'Change Password'}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminManagement;
